import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../config';
import SharedNav from './SharedNav';
import './IntroTexts.css';

export default function IntroTexts({ user, onLogout }) {
  const isAdmin = user?.role?.toLowerCase() === 'admin';
  const [candidates, setCandidates] = useState([]);
  const [sendWindowOpen, setSendWindowOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [recruiters, setRecruiters] = useState([]);
  const [recruiterFilter, setRecruiterFilter] = useState('all');
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const load = useCallback(async () => {
    try {
      const params = isAdmin && recruiterFilter !== 'all' ? { recruiterId: recruiterFilter } : {};
      const { data } = await axios.get(`${API_URL}/api/intro-sms/queue`, { ...authHeaders(), params });
      setCandidates(data.candidates || []);
      setSendWindowOpen(data.sendWindowOpen);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, recruiterFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API_URL}/api/users/recruiters`, authHeaders())
      .then(({ data }) => setRecruiters(data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const endpointBase = (triggerType) => {
    if (triggerType === 'fadv_sent') return 'fadv-sms';
    if (triggerType === 'drug_physical') return 'drug-sms';
    return 'intro-sms';
  };

  const TRIGGER_LABELS = { intro_text: 'Rated 5+', fadv_sent: 'FADV Sent', drug_physical: 'Drug Test Pending' };

  const handleApprove = async (id, triggerType) => {
    const rowKey = `${triggerType}-${id}`;
    setBusyId(rowKey);
    setError('');
    try {
      await axios.post(`${API_URL}/api/${endpointBase(triggerType)}/${id}/approve`, {}, authHeaders());
      setCandidates(prev => prev.filter(c => !(c.id === id && c.trigger_type === triggerType)));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send');
    } finally {
      setBusyId(null);
    }
  };

  const handleSkip = async (id, triggerType) => {
    const rowKey = `${triggerType}-${id}`;
    setBusyId(rowKey);
    setError('');
    try {
      await axios.post(`${API_URL}/api/${endpointBase(triggerType)}/${id}/skip`, {}, authHeaders());
      setCandidates(prev => prev.filter(c => !(c.id === id && c.trigger_type === triggerType)));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to skip');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="intro-texts-page">
      <SharedNav user={user} onLogout={onLogout} />
      <div className="intro-texts-content">
        {isAdmin && (
          <div className="it-header-row">
            <select
              className="it-recruiter-filter"
              value={recruiterFilter}
              onChange={(e) => setRecruiterFilter(e.target.value)}
              style={{ marginLeft: 'auto' }}
            >
              <option value="all">All Recruiters</option>
              {recruiters.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className={`it-window-banner ${sendWindowOpen ? 'it-window-open' : 'it-window-closed'}`}>
          {sendWindowOpen
            ? '✅ These candidates send automatically (every ~20 min, 8:30am–5:30pm Eastern) — Skip anyone you don’t want texted. "Send Now" jumps the queue if you want one out immediately.'
            : '⏸ Outside the send window (8:30am–5:30pm Eastern). These will send automatically once the window opens — Skip now to exclude anyone.'}
        </div>

        {error && <div className="it-error">{error}</div>}

        {loading ? (
          <div>Loading…</div>
        ) : candidates.length === 0 ? (
          <div className="it-empty">Nothing queued right now.</div>
        ) : (
          <div className="mtn-scroll-x">
          <table className="it-table">
            <thead>
              <tr>
                <th>Trigger</th>
                <th>Candidate</th>
                <th>Company</th>
                <th>Recruiter</th>
                <th>Rating</th>
                <th>Message Preview</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map(c => {
                const rowKey = `${c.trigger_type}-${c.id}`;
                const busy = busyId === rowKey;
                return (
                  <tr key={rowKey}>
                    <td>
                      <span className={`it-trigger-badge it-trigger-badge--${c.trigger_type}`}>
                        {TRIGGER_LABELS[c.trigger_type] || c.trigger_type}
                      </span>
                    </td>
                    <td>{c.candidate_name}</td>
                    <td>{c.company_name || '-'}</td>
                    <td>
                      {c.recruiter_name || '-'}
                      {!c.recruiter_connected && (
                        <span className="it-not-connected" title="This recruiter hasn't connected RingCentral — sending will fail until they do">
                          ⚠ not connected
                        </span>
                      )}
                    </td>
                    <td>{c.mountain_rating}</td>
                    <td className="it-preview-cell">{c.preview}</td>
                    <td className="it-actions-cell">
                      <button
                        className="it-skip-btn"
                        disabled={busy}
                        onClick={() => handleSkip(c.id, c.trigger_type)}
                      >
                        Skip
                      </button>
                      <button
                        className="it-approve-btn it-approve-btn--secondary"
                        disabled={!sendWindowOpen || busy || !c.recruiter_connected}
                        onClick={() => handleApprove(c.id, c.trigger_type)}
                        title="Optional — sends immediately instead of waiting for the automatic run"
                      >
                        {busy ? '…' : 'Send Now'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
